// @ts-strict-ignore
import { communicationService } from '@ad-engine/communication';
import {
	AdSlotEvent,
	AdSlotStatus,
	Dictionary,
	slotService,
	utils,
	type AdSlot,
} from '@ad-engine/core';
import { EventOptions } from '../../communication/events/event-options';
import {
	AD_ENGINE_VIDEO_LEARN_MORE_CLICKED,
	AD_ENGINE_VIDEO_OVERLAY_CLICKED,
	AD_ENGINE_VIDEO_TOGGLE_UI_OVERLAY_CLICKED,
} from '../../communication/events/events-ad-engine-video';
import { slotPropertiesTrackingCompiler, slotTrackingCompiler } from '../compilers';
import { BaseTracker, BaseTrackerInterface } from './base-tracker';
import { GAM_INTERSTITIAL_LOADED } from '../../communication/events/events-gam';

const logGroup = 'ad-click-tracker';
const celtraBannerId = 'celtra-object-37';
const celtraInterstitialSelector = '#celtra-banner .celtra-screen-container';

class AdClickTracker extends BaseTracker implements BaseTrackerInterface {
	private eventsToRegister = [
		AD_ENGINE_VIDEO_LEARN_MORE_CLICKED,
		AD_ENGINE_VIDEO_OVERLAY_CLICKED,
		AD_ENGINE_VIDEO_TOGGLE_UI_OVERLAY_CLICKED,
	];
	compilers = [slotTrackingCompiler, slotPropertiesTrackingCompiler];

	register(callback): void {
		communicationService.onSlotEvent(AdSlotEvent.SLOT_RENDERED_EVENT, ({ adSlotName }) => {
			this.addClickTrackingListeners(callback, adSlotName);
		});

		communicationService.on(GAM_INTERSTITIAL_LOADED, () => {
			this.addCeltraInterstitialClickTrackingListener(callback);
		});

		this.eventsToRegister.map((event) => this.addToTracking(event, callback));
	}

	private addToTracking(event: EventOptions, callback: (data: Dictionary) => void): void {
		communicationService.on(
			event,
			({ adSlotName, ad_status }) => {
				callback(this.compileData(slotService.get(adSlotName), null, { ad_status }));
			},
			false,
		);
	}

	private addClickTrackingListeners(callback: (data: Dictionary) => void, slotName): void {
		this.clickDetection(slotName, callback);
	}

	private clickDetection(slotName: string, callback: (data: Dictionary) => void) {
		const eventHandler = () => {
			const elem = document.activeElement;
			if (!elem) {
				return;
			}

			const currentPlacementId = elem.closest(`div#${slotName}`) !== null;
			if (!currentPlacementId) {
				return;
			}

			utils.logger(logGroup, `Click! on slot='${slotName}' is detected.`);

			const adSlot = slotService.get(slotName);
			this.handleClickEvent(callback, adSlot);
			setTimeout(() => {
				(document.activeElement as HTMLBodyElement).blur();
			}, 100);
		};

		window.addEventListener('blur', eventHandler);
	}

	private addCeltraInterstitialClickTrackingListener(callback): void {
		const observer = new MutationObserver(() => {
			const celtraIframe = document.querySelector('.notranslate > iframe') as HTMLIFrameElement;
			if (celtraIframe) {
				const celtraBanner = celtraIframe.contentWindow.document.querySelector(
					celtraInterstitialSelector,
				);
				if (celtraBanner) {
					celtraBanner.addEventListener('click', (event) => {
						this.handleClickEvent(
							callback,
							slotService.get('top_leaderboard'),
							event as MouseEvent,
						);
					});
					observer.disconnect();
				}
			}
		});
		observer.observe(document, {
			childList: true,
			subtree: true,
		});
	}

	private handleClickEvent(
		callback: (data: Dictionary) => void,
		slot: AdSlot,
		event?: MouseEvent,
	): void {
		const data = {
			ad_status: AdSlotStatus.STATUS_CLICKED,
		};

		if (event) {
			const target = event.target as HTMLElement;

			if (target.id === celtraBannerId) {
				const clickData = {
					click: { x: event.clientX, y: event.clientY },
					size: { x: target.offsetWidth, y: target.offsetHeight },
				};
				clickData['click'] = { x: event.offsetX, y: event.offsetY };
				data['click_position'] = JSON.stringify(clickData);
			}
		}

		callback(this.compileData(slot, null, data));
	}
}

export const adClickTracker = new AdClickTracker();
